/**
 * Reglas del inicio de la Tienda. La RPC trae los hechos; aquí se decide
 * qué tarea se enseña, con qué urgencia, y cómo se suman las ventas.
 */

import { catalogSyncStaleMaxHours, isStaleSync } from '../catalog/catalogSyncFreshness';
import { waitDays, waitTone, type WaitTone } from '../orders/orderListModel';

export const COUNTED_SALE_STATUSES = new Set(['paid', 'processing', 'shipped', 'delivered', 'completed']);

export const MERCH_ORIGIN_LABEL = 'Stock propio (merch AiPeces)';

export interface SaleLine {
    originKey: string;
    originLabel: string;
    status: string;
    cents: number;
    orderId: string;
    paidAt: string;
}

export interface OriginSales {
    key: string;
    label: string;
    monthCents: number;
    monthOrders: number;
    allCents: number;
    allOrders: number;
}

export interface HomeSupplier {
    slug: string;
    name: string;
    isActive: boolean;
    lastOkFinishedAt?: string | null;
}

export interface BrandCollision {
    brand: string;
    supplierName: string;
    supplierSlug: string;
    /** false: el proveedor no debe aparecer como marca. JLC (true) no entra. */
    showBrand?: boolean;
    productCount: number;
}

export type TaskTone = 'aviso' | 'critico' | 'neutro';

export interface HomeTask {
    id: string;
    count: number;
    title: string;
    detail: string;
    badge: string;
    tone: TaskTone;
    href: string;
}

export type SalesPeriod = 'month' | 'all';

function monthStartMs(now: Date): number {
    return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1);
}

/** Suma importes de línea. Un reembolso (estado refunded) no cuenta. El mes va por paidAt, en UTC. */
export function summarizeOriginSales(lines: readonly SaleLine[], now: Date): OriginSales[] {
    const start = monthStartMs(now);
    const grouped = new Map<string, OriginSales & { monthIds: Set<string>; allIds: Set<string> }>();

    for (const line of lines) {
        if (!COUNTED_SALE_STATUSES.has(line.status)) continue;
        let row = grouped.get(line.originKey);
        if (!row) {
            row = {
                key: line.originKey,
                label: line.originLabel,
                monthCents: 0,
                monthOrders: 0,
                allCents: 0,
                allOrders: 0,
                monthIds: new Set(),
                allIds: new Set(),
            };
            grouped.set(line.originKey, row);
        }
        row.allCents += line.cents;
        row.allIds.add(line.orderId);
        const paidAt = new Date(line.paidAt).getTime();
        if (Number.isFinite(paidAt) && paidAt >= start) {
            row.monthCents += line.cents;
            row.monthIds.add(line.orderId);
        }
    }

    return [...grouped.values()].map((row) => ({
        key: row.key,
        label: row.label,
        monthCents: row.monthCents,
        monthOrders: row.monthIds.size,
        allCents: row.allCents,
        allOrders: row.allIds.size,
    }));
}

/** El merch va primero. Un proveedor activo sin ventas se queda en 0; uno inactivo y a cero no. */
export function salesWithActiveSuppliers(
    rows: readonly OriginSales[],
    suppliers: readonly HomeSupplier[],
): OriginSales[] {
    const byKey = new Map(rows.map((row) => [row.key, row]));
    for (const supplier of suppliers) {
        if (!supplier.isActive || byKey.has(supplier.slug)) continue;
        byKey.set(supplier.slug, {
            key: supplier.slug,
            label: supplier.name,
            monthCents: 0,
            monthOrders: 0,
            allCents: 0,
            allOrders: 0,
        });
    }
    return [...byKey.values()].sort((a, b) => {
        if (a.key === 'merch') return -1;
        if (b.key === 'merch') return 1;
        return b.allCents - a.allCents || a.label.localeCompare(b.label, 'es');
    });
}

export function salesInPeriod(
    rows: readonly OriginSales[],
    period: SalesPeriod,
): { key: string; label: string; cents: number; orders: number }[] {
    return rows.map((row) => ({
        key: row.key,
        label: row.label,
        cents: period === 'month' ? row.monthCents : row.allCents,
        orders: period === 'month' ? row.monthOrders : row.allOrders,
    }));
}

export function shipmentUrgency(oldestPaidAt: string | null | undefined, now: Date): {
    days: number | null;
    tone: WaitTone | null;
} {
    const days = waitDays(oldestPaidAt, 'paid', null, now);
    return { days, tone: waitTone(days) };
}

export function staleActiveSuppliers(suppliers: readonly HomeSupplier[], now: Date): HomeSupplier[] {
    return suppliers.filter((supplier) => {
        if (!supplier.isActive) return false;
        const maxHours = catalogSyncStaleMaxHours(supplier.slug);
        return isStaleSync(supplier.lastOkFinishedAt, maxHours, now.getTime());
    });
}

function toneBadge(tone: WaitTone | null): { badge: string; tone: TaskTone } {
    if (tone === 'critico') return { badge: 'Crítico', tone: 'critico' };
    if (tone === 'aviso') return { badge: 'Aviso', tone: 'aviso' };
    return { badge: 'Reciente', tone: 'neutro' };
}

export function buildHomeTasks(input: {
    unshippedCount: number;
    oldestUnshippedPaidAt?: string | null;
    abandonedCount: number;
    suppliers: readonly HomeSupplier[];
    incompletePublished: number;
    brandCollisions: readonly BrandCollision[];
    openReports: number;
    lowStockCount?: number;
    lotPendingCount?: number;
    pendingBrandCount?: number;
    missingManufacturerCount?: number;
    now: Date;
    formatDate: (iso: string) => string;
}): HomeTask[] {
    const tasks: HomeTask[] = [];
    if (input.unshippedCount > 0) {
        const urgency = shipmentUrgency(input.oldestUnshippedPaidAt, input.now);
        const badge = toneBadge(urgency.tone);
        const days = urgency.days;
        tasks.push({
            id: 'unshipped',
            count: input.unshippedCount,
            title: 'Pedidos pagados sin enviar',
            detail: days == null
                ? 'Sin fecha de pago en el más antiguo'
                : `El más antiguo lleva ${days} ${days === 1 ? 'día' : 'días'}`,
            badge: badge.badge,
            tone: badge.tone,
            href: '/store/orders',
        });
    }
    if (input.abandonedCount > 0) {
        tasks.push({
            id: 'abandoned',
            count: input.abandonedCount,
            title: 'Checkouts abandonados',
            detail: 'Pendientes de pago con más de 24 horas',
            badge: 'Aviso',
            tone: 'aviso',
            href: '/store/orders?view=abandoned',
        });
    }
    const stale = staleActiveSuppliers(input.suppliers, input.now);
    if (stale.length > 0) {
        tasks.push({
            id: 'stale-sync',
            count: stale.length,
            title: 'Proveedores con sincronización caducada',
            detail: stale.map((supplier) => (
                supplier.lastOkFinishedAt
                    ? `${supplier.name}, última sync correcta el ${input.formatDate(supplier.lastOkFinishedAt)}`
                    : `${supplier.name}, sin sync correcta`
            )).join(' · '),
            badge: 'Caducada',
            tone: 'critico',
            href: stale.length === 1
                ? `/store/suppliers/${stale[0]?.slug}?sub=conexion`
                : '/store/suppliers?view=stale',
        });
    }
    if (input.incompletePublished > 0) {
        tasks.push({
            id: 'incomplete-fichas',
            count: input.incompletePublished,
            title: 'Fichas publicadas incompletas',
            detail: 'Publicadas en tienda y con menos de 4 criterios',
            badge: 'Incompletas',
            tone: 'aviso',
            href: '/store/products?view=incomplete',
        });
    }
    const hiddenBrands = input.brandCollisions.filter((collision) => (
        collision.showBrand === false && collision.productCount > 0
    ));
    if (hiddenBrands.length > 0) {
        const count = hiddenBrands.reduce((sum, collision) => sum + collision.productCount, 0);
        tasks.push({
            id: 'brand-supplier',
            count,
            title: 'Productos con marca = proveedor',
            detail: hiddenBrands.map((collision) => (
                `${collision.brand} (${collision.supplierName})`
            )).join(' · '),
            badge: 'Revisar',
            tone: 'aviso',
            href: '/store/products?view=brand',
        });
    }
    if ((input.lowStockCount ?? 0) > 0) {
        tasks.push({
            id: 'merch-stock',
            count: input.lowStockCount ?? 0,
            title: 'Tallas de merch agotadas o con poco stock',
            detail: 'Disponible en cero o por debajo del umbral de aviso',
            badge: 'Stock',
            tone: 'aviso',
            href: '/store/products/stock',
        });
    }
    if ((input.pendingBrandCount ?? 0) > 0) {
        tasks.push({
            id: 'brands-pending',
            count: input.pendingBrandCount ?? 0,
            title: 'Marcas de producto por asignar',
            detail: 'El producto sigue publicado; falta elegir la marca',
            badge: 'Asignar',
            tone: 'aviso',
            href: '/store/settings/brands',
        });
    }
    if ((input.missingManufacturerCount ?? 0) > 0) {
        tasks.push({
            id: 'brands-gpsr',
            count: input.missingManufacturerCount ?? 0,
            title: 'Marcas con productos publicados y sin datos de fabricante',
            detail: 'Falta el nombre legal, la dirección o el email de contacto',
            badge: 'Fabricante',
            tone: 'aviso',
            href: '/store/settings/brands',
        });
    }
    if ((input.lotPendingCount ?? 0) > 0) {
        tasks.push({
            id: 'lot-sizes',
            count: input.lotPendingCount ?? 0,
            title: 'Pedidos de lote con talla por asignar',
            detail: 'El pedido no trae la talla de cada prenda',
            badge: 'Asignar',
            tone: 'aviso',
            href: '/store/products/stock',
        });
    }
    if (input.openReports > 0) {
        tasks.push({
            id: 'second-life-reports',
            count: input.openReports,
            title: 'Denuncias abiertas de Segunda Vida',
            detail: 'Anuncios denunciados que siguen abiertos',
            badge: 'Abiertas',
            tone: 'critico',
            href: '/store/second-life',
        });
    }
    return tasks;
}
